import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { requestJson } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

export type Rider = {
  memberId: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  vehicleType: string | null;
  licensePlate: string | null;
  photoUrl: string | null;
  status: "active" | "suspended" | "offline" | null;
  createdAt: string;
};

export type AssignableRider = {
  memberId: string;
  userId: string;
  name: string;
  email: string;
  vehicleType: string | null;
  licensePlate: string | null;
  status: "active";
};

export function useRiders() {
  return useQuery({
    queryKey: queryKeys.riders.all(),
    queryFn: () => requestJson<{ riders: Rider[] }>("/api/orders/riders"),
    select: (data) => data.riders,
  });
}

export function useAssignableRiders(orderId?: string) {
  return useQuery({
    // Scoped per Order: the list excludes Riders who already refused that
    // Order's Delivery, so it is not the same list for every Order.
    queryKey: queryKeys.riders.assignableForOrder(orderId),
    queryFn: () =>
      requestJson<{ riders: AssignableRider[] }>(
        orderId
          ? `/api/orders/riders/assignable?orderId=${encodeURIComponent(orderId)}`
          : "/api/orders/riders/assignable",
      ),
    select: (data) => data.riders,
  });
}

export function useInviteRider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      requestJson<{ invitation: unknown }>("/api/orders/riders/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.riders.all() });
    },
  });
}

export function useUpdateRider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      memberId,
      input,
    }: {
      memberId: string;
      input: Partial<Pick<Rider, "vehicleType" | "licensePlate" | "photoUrl">>;
    }) =>
      requestJson<{ rider: Rider }>(`/api/orders/riders/${memberId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.riders.all() });
    },
  });
}

export function useSuspendRider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      requestJson<{ rider: Rider }>(`/api/orders/riders/${memberId}/suspend`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.riders.all() });
    },
  });
}

export function useRestoreRider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      requestJson<{ rider: Rider }>(`/api/orders/riders/${memberId}/restore`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.riders.all() });
    },
  });
}

export function useRemoveRider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      requestJson<{ ok: boolean }>(`/api/orders/riders/${memberId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.riders.all() });
    },
  });
}
